import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type TaskStatus = "open" | "in_progress" | "done" | "blocked" | "cancelled";
export type TaskPriority = "low" | "medium" | "high" | "critical";
export type TaskCategory = "execution" | "validation" | "communication" | "monitoring" | "risk_mitigation" | "preparation";

export interface ActionTask {
  id: string;
  session_id: string;
  workspace_id: string;
  title: string;
  description: string | null;
  assignee_id: string | null;
  due_date: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  category: TaskCategory;
  source: string | null;
  depends_on: string[];
  completion_criteria: string | null;
  completed_at: string | null;
  completed_by: string | null;
  order_index: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Joined
  session?: { summary: string | null; decision_type: string | null };
  assignee?: { name: string | null; email: string } | null;
}

interface CreateTaskInput {
  session_id: string;
  workspace_id: string;
  title: string;
  description?: string;
  priority?: TaskPriority;
  category?: TaskCategory;
  due_date?: string;
  assignee_id?: string;
  source?: string;
  created_by: string;
}

interface UpdateTaskInput {
  id: string;
  status?: TaskStatus;
  title?: string;
  description?: string;
  priority?: TaskPriority;
  due_date?: string;
  assignee_id?: string;
  completion_criteria?: string;
}

export function useActionTasks(workspaceId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["action-tasks", workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("action_tasks")
        .select(`*, session:decision_sessions(summary, decision_type), assignee:profiles!action_tasks_assignee_id_fkey(name, email)`)
        .eq("workspace_id", workspaceId)
        .order("order_index", { ascending: true });
      if (error) throw error;
      return data as ActionTask[];
    },
    enabled: !!workspaceId,
  });

  const createMutation = useMutation({
    mutationFn: async (input: CreateTaskInput) => {
      const { data, error } = await supabase
        .from("action_tasks")
        .insert(input)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["action-tasks", workspaceId] });
      toast({ title: "Task erstellt" });
    },
    onError: (error: Error) => {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateTaskInput) => {
      const { id, ...updates } = input;
      const updateData: Record<string, unknown> = { ...updates };
      if (updates.status === "done") {
        updateData.completed_at = new Date().toISOString();
        const { data: { user } } = await supabase.auth.getUser();
        if (user) updateData.completed_by = user.id;
      }
      const { data, error } = await supabase
        .from("action_tasks")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["action-tasks", workspaceId] });
      toast({ title: "Task aktualisiert" });
    },
    onError: (error: Error) => {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("action_tasks").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["action-tasks", workspaceId] });
      toast({ title: "Task gelöscht" });
    },
    onError: (error: Error) => {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    },
  });

  const importFromReport = async (
    sessionId: string,
    workspaceId: string,
    tasks: Array<{ title: string; description?: string; category?: TaskCategory; priority?: TaskPriority }>,
    userId: string
  ) => {
    const { data: existing } = await supabase
      .from("action_tasks")
      .select("title")
      .eq("session_id", sessionId);

    const existingTitles = new Set(existing?.map(e => e.title) || []);
    const newTasks = tasks.filter(t => !existingTitles.has(t.title));

    if (newTasks.length === 0) {
      toast({ title: "Bereits importiert", description: "Alle Tasks sind bereits vorhanden." });
      return 0;
    }

    const inserts = newTasks.map((task, i) => ({
      session_id: sessionId,
      workspace_id: workspaceId,
      title: task.title,
      description: task.description || null,
      category: task.category || "execution" as TaskCategory,
      priority: task.priority || "medium" as TaskPriority,
      source: "report",
      order_index: i,
      created_by: userId,
    }));

    const { error } = await supabase.from("action_tasks").insert(inserts);
    if (error) throw error;

    queryClient.invalidateQueries({ queryKey: ["action-tasks", workspaceId] });
    toast({ title: "Tasks importiert", description: `${newTasks.length} Tasks hinzugefügt.` });
    return newTasks.length;
  };

  return {
    tasks: query.data || [],
    isLoading: query.isLoading,
    createTask: createMutation.mutate,
    updateTask: updateMutation.mutate,
    deleteTask: deleteMutation.mutate,
    importFromReport,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
}
